import {
  Bot,
  ChevronDown,
  ChevronRight,
  CreditCard,
  FileText,
  ScrollText,
  Settings,
  Users,
  Zap,
} from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/ui/collapsible'
import { Label } from '@/shared/components/ui/label'

interface EventSelectorProps {
  eventCategories: Record<string, string[]>
  selectedEvents: string[]
  onSelectionChange: (events: string[]) => void
}

const CATEGORY_META: Record<string, { label: string; icon: React.ReactNode }> =
  {
    agent: { label: 'Agents', icon: <Bot className='w-4 h-4' /> },
    event: { label: 'Events', icon: <Zap className='w-4 h-4' /> },
    invoice: { label: 'Invoices', icon: <FileText className='w-4 h-4' /> },
    payment: { label: 'Payments', icon: <CreditCard className='w-4 h-4' /> },
    contract: { label: 'Contracts', icon: <ScrollText className='w-4 h-4' /> },
    customer: { label: 'Customers', icon: <Users className='w-4 h-4' /> },
    usage: { label: 'Usage', icon: <Settings className='w-4 h-4' /> },
    billing: { label: 'Billing', icon: <CreditCard className='w-4 h-4' /> },
  }

function formatEventName(event: string): string {
  // "agent.task_completed" -> "Task Completed"
  const parts = event.split('.')
  const name = parts.slice(1).join(' ')
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export function EventSelector({
  eventCategories,
  selectedEvents,
  onSelectionChange,
}: EventSelectorProps) {
  const categories = Object.keys(eventCategories)
  const [expandedGroups, setExpandedGroups] = useState<string[]>(categories)

  const handleEventToggle = (event: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedEvents, event])
    } else {
      onSelectionChange(selectedEvents.filter(e => e !== event))
    }
  }

  const handleGroupToggle = (category: string, checked: boolean) => {
    const groupEvents = eventCategories[category]
    if (checked) {
      onSelectionChange([...new Set([...selectedEvents, ...groupEvents])])
    } else {
      const groupSet = new Set(groupEvents)
      onSelectionChange(selectedEvents.filter(e => !groupSet.has(e)))
    }
  }

  const toggleGroup = (category: string) => {
    setExpandedGroups(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    )
  }

  const isGroupSelected = (category: string) =>
    eventCategories[category].every(e => selectedEvents.includes(e))

  const isGroupPartiallySelected = (category: string) => {
    const events = eventCategories[category]
    const count = events.filter(e => selectedEvents.includes(e)).length
    return count > 0 && count < events.length
  }

  const totalCount = Object.values(eventCategories).flat().length

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div>
          <h3 className='font-medium'>Select Events</h3>
          <p className='text-sm text-muted-foreground'>
            Choose which events to send to your webhook endpoint
          </p>
        </div>
        <div className='text-sm text-muted-foreground'>
          {selectedEvents.length} of {totalCount} selected
        </div>
      </div>

      <div className='border rounded-lg'>
        {categories.map((category, index) => {
          const events = eventCategories[category]
          const meta = CATEGORY_META[category] || {
            label: category.charAt(0).toUpperCase() + category.slice(1),
            icon: <Settings className='w-4 h-4' />,
          }

          return (
            <div key={category} className={`${index > 0 ? 'border-t' : ''}`}>
              <Collapsible
                open={expandedGroups.includes(category)}
                onOpenChange={() => toggleGroup(category)}
              >
                <div className='flex items-center space-x-2 p-3 hover:bg-muted/50'>
                  <CollapsibleTrigger asChild>
                    <Button variant='ghost' size='sm' className='h-6 w-6 p-0'>
                      {expandedGroups.includes(category) ? (
                        <ChevronDown className='w-4 h-4' />
                      ) : (
                        <ChevronRight className='w-4 h-4' />
                      )}
                    </Button>
                  </CollapsibleTrigger>

                  <Checkbox
                    id={`group-${category}`}
                    checked={isGroupSelected(category)}
                    onCheckedChange={checked =>
                      handleGroupToggle(category, checked as boolean)
                    }
                    className='data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground'
                    {...(isGroupPartiallySelected(category) && {
                      'data-state': 'indeterminate',
                    })}
                  />

                  <div className='flex items-center gap-2 flex-1'>
                    {meta.icon}
                    <Label
                      htmlFor={`group-${category}`}
                      className='font-medium cursor-pointer flex-1'
                    >
                      {meta.label}
                    </Label>
                    <span className='text-sm text-muted-foreground'>
                      {events.filter(e => selectedEvents.includes(e)).length} /{' '}
                      {events.length}
                    </span>
                  </div>
                </div>

                <CollapsibleContent>
                  <div className='pl-8 pr-3 pb-3 space-y-2'>
                    {events.map(event => (
                      <div
                        key={event}
                        className='flex items-center space-x-2 py-1'
                      >
                        <Checkbox
                          id={`event-${event}`}
                          checked={selectedEvents.includes(event)}
                          onCheckedChange={checked =>
                            handleEventToggle(event, checked as boolean)
                          }
                        />
                        <div className='flex-1'>
                          <Label
                            htmlFor={`event-${event}`}
                            className='cursor-pointer flex-1'
                          >
                            {formatEventName(event)}
                          </Label>
                          <div className='text-xs text-muted-foreground mt-1 font-mono'>
                            {event}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          )
        })}
      </div>

      {selectedEvents.length === 0 && (
        <div className='text-center py-8 text-muted-foreground'>
          <Zap className='w-8 h-8 mx-auto mb-2 opacity-50' />
          <p className='text-sm'>
            Select at least one event to enable webhook notifications
          </p>
        </div>
      )}
    </div>
  )
}
